import axios from "axios";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Alumni, AlumniBase, AlumniExtended, Roles } from "iasma-datatypes";
import { useAuth } from "@/contexts/AuthContext"
import { useNotify } from "@/contexts/NotifyContext/useNotify";
import { getModifiedFields, mergeAlumni, queryAlumni, queryAlumniList, queryNames, updateAlumni } from "@/services/DirectoryService";
import { Profile } from "@/components/directories/profile/Profile";
import { useVersionCheck } from "@/contexts/VersionCheckContext";
import { useParams } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { ANGKATAN_PERTAMA, SELF_ENTRY_INDEX_THRESHOLD } from "iasma-datatypes";
import { Loading } from "../Loading";
import { useIASMA } from "@/contexts/IASMAContext";
import { getLocalTime, IconTombStone } from "@/constants";

type Breakpoint = "xs" | "sm" | "md" | "lg";

interface ColumnDef {
	key: keyof Alumni;
	label: string;
	/** Smallest breakpoint at which this column becomes visible. */
	minBreakpoint: Breakpoint;
	/** If true, only admins ever see this column, at any breakpoint. */
	adminOnly?: boolean;
	/** Optional custom cell renderer. */
	render?: (row: Alumni) => React.ReactNode;
	/** Right/left align, mainly for numeric-ish columns. */
	align?: "left" | "right" | "center";
}

/**
 * ─────────────────────────────────────────────────────────────────────────
 * COLUMN CONFIG — the single source of truth
 * Add/remove/reorder columns here; visibility follows automatically.
 * ─────────────────────────────────────────────────────────────────────────
 */
const COLUMNS: ColumnDef[] = [
	{ key: "Name", label: "Nama", minBreakpoint: "xs", 
		render:(alumni) => {
			if (alumni.DOD && (alumni.DOD !== "")) {
				return (
					<div className="flex min-w-0 items-center gap-1 overflow-hidden">
						<span className="min-w-0 truncate">{alumni.Name}</span>
						<span className="flex shrink-0 items-center gap-0.5 text-slate-400">
							<IconTombStone />
							{alumni.DOD === "1900-01-01" ? "" : alumni.DOD}
						</span>
					</div>
				)
			}
			return <span className="block min-w-0 truncate">{alumni.Name}</span>
		} 
	},
	{ key: "Graduation", label: "Angkatan", minBreakpoint: "xs", align: "center" },

	// sm and up (admin only)
	{ key: "Classes", label: "Kelas", minBreakpoint: "sm", adminOnly: true },

	// md and up (admin only)
	{ key: "Alias", label: "Nickname", minBreakpoint: "md", adminOnly: true },
	{ key: "NIS", label: "NIS", minBreakpoint: "md", adminOnly: true },
	//   { key: "pekerjaan", label: "Pekerjaan", minBreakpoint: "md", adminOnly: true },

	// lg and up (admin only)
	{ key: "Title", label: "Titel", minBreakpoint: "lg", adminOnly: true },
	{ key: "Phones", label: "Handphone", minBreakpoint: "lg", adminOnly: true },
	{ key: "BirthDate", label: "Lahir", minBreakpoint: "lg", adminOnly: true },
];

/** Tailwind visibility classes per breakpoint. `mobile` = always visible. */
const BREAKPOINT_CLASS: Record<Breakpoint, string> = {
	xs: "table-cell",
	sm: "hidden sm:table-cell",
	md: "hidden md:table-cell",
	lg: "hidden lg:table-cell",
};

export const ViewAlumni: React.FC = () => {
	const { graduation } = useParams()
	const { session, setToken } = useAuth()
	const { setNotify } = useNotify();
	const { receiveVersion } = useVersionCheck();
	const { angkatan, setAngkatan, alumniList, setAlumniList, mergeRecord, setMergeRecord } = useIASMA();
	

	const [isAdmin, setIsAdmin] = useState<boolean>(false);

	const [searchTerm, setSearchTerm] = useState('');
	const [filteredAlumniList, setFilteredAlumniList] = useState<Alumni[]>([]);

	const [currentAlumniId, setCurrentAlumniId] = useState(0);
	const [currentProfile, setCurrentProfile] = useState<AlumniExtended | undefined>(undefined);
	const tableScrollRef = useRef<HTMLDivElement | null>(null);
	const savedScrollTopRef = useRef(0);

	const now = new Date();
	const currentMonth = now.getMonth() + 1; // Month (1–12)
	const currentYear = now.getFullYear() - (currentMonth < 7 ? 1 : 0); // Academic year starts in July

	const years = Array.from({ length: currentYear - ANGKATAN_PERTAMA + 1 }, (_, i) => currentYear - i);

	const visibleColumns = useMemo(
		() => COLUMNS.filter((col) => isAdmin || !col.adminOnly),
		[isAdmin]
	);

	useEffect(() => {
		setAngkatan(graduation ? parseInt(graduation) : session?.User?.Alumni?.Graduation!)
	}, [])

	useEffect(() => {
		const checkAdmin = async () => {
			if (session!.Roles.indexOf(Roles.ADMIN_ALL) >= 0) {
				setIsAdmin(true);
			} else if (session!.Roles.indexOf(Roles.ADMIN_ANGKATAN) >= 0) {
				if (session!.User!.Alumni!.Graduation === angkatan) {
					setIsAdmin(true);
				}
			}
		}


		setNotify(null);

		if (angkatan >= ANGKATAN_PERTAMA) {
			checkAdmin();
		} else {
			// all angkatan mulai dari kosong
			setFilteredAlumniList([]);
		}
	}, [angkatan]);

	useEffect(() => {
		// angkatan tertentu
		if (angkatan >= ANGKATAN_PERTAMA) {
			// setSelectedIds([])
			setFilteredAlumniList(
				alumniList.filter(alumnus =>
					alumnus.Name.toLowerCase().includes(searchTerm.toLowerCase())
					|| (alumnus.Title && alumnus.Title.toLowerCase().includes(searchTerm.toLowerCase()))
					|| (alumnus.Alias && alumnus.Alias.toLowerCase().includes(searchTerm.toLowerCase()))
					|| (alumnus.Classes && alumnus.Classes.toLowerCase().includes(searchTerm.toLowerCase()))
				)
			);
		}
	}, [alumniList, searchTerm]);

	useEffect(() => {
		const searchAlumnies = async () => {
			try {
				setToken(axios);
				const result = await queryNames(searchTerm);
				console.log(result);
				setAlumniList(result.rows);
			} catch (error: any) {
				setNotify({
					title: "Error Fetching Names",
					error,
					severity: 'error'
				});
			}
		};

		// semua angkatan
		if ((angkatan < ANGKATAN_PERTAMA) && (searchTerm.length >= 2)) {
			// setSelectedIds([])
			setFilteredAlumniList(alumniList);
			searchAlumnies();
		}

	}, [searchTerm]);

	useEffect(() => {
		const fetchAlumnus = async () => {
			try {
				setNotify(null);
				if (currentAlumniId) {
					setToken(axios);
					// retrieve full data
					const result: AlumniExtended = await queryAlumni(currentAlumniId, receiveVersion);
					setCurrentProfile(result);
				}
			} catch (error: any) {
				setNotify({ title: "Error Fetching Alumni", error, severity: 'error' });
			}
		};
		fetchAlumnus();
	}, [currentAlumniId, receiveVersion]);

	useLayoutEffect(() => {
		if (currentAlumniId === 0 && tableScrollRef.current) {
			tableScrollRef.current.scrollTop = savedScrollTopRef.current;
		}
	}, [currentAlumniId, filteredAlumniList]);

	const captureTableScroll = () => {
		if (tableScrollRef.current) {
			savedScrollTopRef.current = tableScrollRef.current.scrollTop;
		}
	};

	const openAlumniProfile = (id: number) => {
		captureTableScroll();
		setCurrentAlumniId(id);
	};

	const profileOnClose = () => {
		captureTableScroll();
		setCurrentAlumniId(0)
		setCurrentProfile(undefined)
	}

	const profileOnUpdate = (update: AlumniExtended) => {
		captureTableScroll();
		setCurrentProfile(update);

		const updatedAlumniList = alumniList.map((alumnus: Alumni) =>
			alumnus.Id === update.Id ? { ...alumnus, ...update } : alumnus
		);
		setAlumniList(updatedAlumniList);

		setFilteredAlumniList((currentList: Alumni[]) =>
			currentList.map((alumnus: Alumni) =>
				alumnus.Id === update.Id ? { ...alumnus, ...update } : alumnus
			)
		);
	}

	const profileOnMergeExecute = async (original: AlumniExtended, tobeKeep: AlumniExtended, tobeDeleted: AlumniExtended) => {
		console.log("onMergeExecute- Keep:", tobeKeep, "Delete:", tobeDeleted)
		captureTableScroll();
		setCurrentAlumniId(0)
		setCurrentProfile(undefined)
		setNotify(null);
		try {
			let data = getModifiedFields<AlumniExtended>(original, tobeKeep);
			if (Object.keys(data).length > 0) {
				if (data.ExtraData)
					data.ExtraData.History[getLocalTime()] = {
						Action: "Data Merge",
						By: session?.User?.Email					
					}
				console.log("onMergeExecute - Modified fields:", data);
				setToken(axios);
				let result = await updateAlumni(original.Id!, data);
				console.log("onMergeExecute - Update result:", result);

				const updatedAlumniList = alumniList.map((alumnus) =>
					alumnus.Id === original.Id ? { ...alumnus, ...data } : alumnus
				);
				setAlumniList(updatedAlumniList);
				setFilteredAlumniList((currentList) =>
					currentList.map((alumnus) =>
						alumnus.Id === original.Id ? { ...alumnus, ...data } : alumnus
					)
				);
			} else {
				console.log("onMergeExecute - original has no changes");
			}

			setToken(axios);
			let result = await mergeAlumni(original.Id, tobeDeleted);
			console.log("onMergeExecute - Merge", tobeDeleted.Id, " to ", original.Id, " result: ", result)

			let newList = alumniList.filter((alumnus) => alumnus.Id !== tobeDeleted.Id);
			setAlumniList(newList);			
			setFilteredAlumniList((currentList) =>
				currentList.filter((alumnus) => alumnus.Id !== tobeDeleted.Id)
			);
		} catch (error: any) {
			setNotify({ title: "onMergeExecute Update Gagal", error, severity: 'error' });
		}

	}

	if (currentAlumniId > 0) {
		if (!currentProfile) {
			return <Loading />;
		} else {
			return <Profile profile={currentProfile} editable={isAdmin} onClose={profileOnClose} onMerge={profileOnMergeExecute} onUpdate={profileOnUpdate} />
		}

	} else if ((alumniList.length === 0) && (angkatan >= ANGKATAN_PERTAMA)) {
		return <Loading />;
	} else {
		return (
			<div className="flex flex-col overflow-hidden bg-(--color-background) md:p-2 sm:p-1">
				<div className="p-2">
					<div className="grid md:grid-cols-2 gap-2 mb-2">
						<select
							value={angkatan}
							onChange={(e) => setAngkatan(Number(e.target.value))}
							className="px-4 py-2 border border-(--color-border) rounded-lg focus:outline-none focus:ring-2 focus:ring-(--color-ring)"
						>
							{years.map(year => (
								<option key={year} value={year}>Angkatan {year}</option>
							))}
							<option key={-1} value={-1}>Semua Angkatan</option>
						</select>
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-(--color-text-secondary)" size={20} />
							<input
								type="text"
								placeholder={angkatan < ANGKATAN_PERTAMA ? "Cari Nama" : "nama, gelar, panggilan, kelas"}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="w-full pl-10 pr-4 py-2 text-(--color-text) border border-(--color-border) rounded-lg focus:outline-none focus:ring-2 focus:ring-(--color-ring)"
							/>
						</div>
					</div>
				</div>
				<div className="overflow-auto" ref={tableScrollRef}>
					<div className="max-h-[calc(100vh-280px)]">
						<Table className="table-fixed w-full">
							<TableHeader className="bg-(--color-surface) text-(--color-text) sticky top-0 z-50">
								<TableRow>
									{visibleColumns.map((col) => (
									<TableHead
										key={col.key}
										className={`${BREAKPOINT_CLASS[col.minBreakpoint]} ${
											col.key === "Name" ? "w-[58%] max-w-0" : col.key === "Graduation" ? "w-[22%] text-center" : ""
										} whitespace-nowrap ${col.align === "right" ? "text-right" : ""}`}
									>
										{col.label}
									</TableHead>
									))}
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredAlumniList.map((alumnus) => (
									<TableRow key={alumnus.Id} 
										className={"hover:bg-(--color-surface-hover)" + (isAdmin && (alumnus.Id > SELF_ENTRY_INDEX_THRESHOLD) ? " bg-gray-200" : "")}
										onClick={() => openAlumniProfile(alumnus.Id)}
									>
										{visibleColumns.map((col) => (
										<TableCell
											key={col.key}
											className={`${BREAKPOINT_CLASS[col.minBreakpoint]} ${
												col.key === "Name" ? "max-w-0 overflow-hidden font-medium text-zinc-900" : col.key === "Graduation" ? "overflow-hidden text-center text-zinc-600" : "text-zinc-600"
												} ${col.align === "right" ? "text-right" : ""}`}											
										>
											{col.render ? col.render(alumnus) : alumnus[col.key] ? String(alumnus[col.key]) : "-"}
										</TableCell>
										))}								
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</div>
			</div>
		)
	}
}